// services/utils.js
import { fetchAuthSession } from 'aws-amplify/auth';

// Helper to get auth headers
export async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens || !session.tokens.idToken) {
      console.error('Authentication Failure: No valid session or token found.');
      throw new Error('No authentication token');
    }
    const headers = {
      Authorization: `Bearer ${session.tokens.idToken.toString()}`
    };
    //console.log('Authorization Headers:', headers);
    return headers;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
}

// Enhanced error handling wrapper
export const handleApiCall = async (apiCall) => {
  try {
    let response = await apiCall();
    //console.log('API Response:', response);
    
    // Check if the response contains a Promise (Amplify v6 pattern)
    if (response && response.response && response.response instanceof Promise) {
      //console.log('Detected Promise in response, awaiting...');
      const actualResponse = await response.response;
      //console.log('Resolved Promise response:', actualResponse);
      
      // Check if the body is a ReadableStream
      if (actualResponse && actualResponse.body && actualResponse.body instanceof ReadableStream) {
        //console.log('Detected ReadableStream, parsing as JSON...');
        try {
          const jsonData = await actualResponse.body.json();
          //console.log('Parsed JSON data:', jsonData);
          return {
            data: jsonData,
            status: actualResponse.statusCode || 200,
            success: true
          };
        } catch (jsonError) {
          console.error('Error parsing JSON from stream:', jsonError);
          // Try text fallback
          const textData = await actualResponse.body.text();
          return {
            data: textData,
            status: actualResponse.statusCode || 200,
            success: true
          };
        }
      }
      
      // If body exists but is not a stream
      if (actualResponse && actualResponse.body) {
        return {
          data: actualResponse.body,
          status: actualResponse.statusCode || 200,
          success: true
        };
      }
      
      return {
        data: actualResponse,
        status: 200,
        success: true
      };
    }
    
    // Check if it's already the response data (AWS Amplify v5+ returns data directly)
    if (response && (response.data !== undefined || response.body !== undefined)) {
      // Extract the actual data
      const data = response.data || response.body;
      
      // Create a normalized response
      return {
        data: data,
        status: response.statusCode || response.$metadata?.httpStatusCode || 200,
        success: true
      };
    }
    
    // If it's already normalized or direct data, return as is
    return {
      data: response,
      status: 200,
      success: true
    };
  } catch (error) {
    console.error('API Error:', error);
    
    // Handle different error formats
    let errorMessage = 'An unexpected error occurred';
    let statusCode = 500;
    let errorData = {};
    
    if (error.response) {
      // AWS Amplify error format
      const errorBody = error.response.body || error.response.data || {};
      errorMessage = errorBody.message || errorBody.error || errorMessage;
      statusCode = error.response.statusCode || error.response.status || statusCode;
      errorData = errorBody;
    } else if (error.$metadata) {
      // AWS SDK error format
      errorMessage = error.message || errorMessage;
      statusCode = error.$metadata.httpStatusCode || statusCode;
      errorData = { code: error.name, message: error.message };
    } else if (error.message) {
      // Standard error format
      errorMessage = error.message;
      errorData = { message: error.message };
    }
    
    return {
      data: null,
      error: errorMessage,
      errorData: errorData,
      status: statusCode,
      success: false
    };
  }
};